// src/components/Water/WaterDashboard.tsx
import React, { useEffect, useState } from 'react';
import {
  fetchWaterData,
  createWaterData,
  getWaterQualityByLocation,
  WaterData,
  WaterQualityByLocationResponse,
  getLocationFromCoordinates,
} from '../../services/api';
import {
  Box,
  Typography,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import NavigationIcon from '@mui/icons-material/Navigation';
import MapIcon from '@mui/icons-material/Map';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddIcon from '@mui/icons-material/Add';
import WaterHeatmap from './WaterHeatmap';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`water-tabpanel-${index}`}
      aria-labelledby={`water-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const WaterDashboard: React.FC = () => {
  const [waterData, setWaterData] = useState<WaterData[]>([]);
  const [formData, setFormData] = useState<WaterData>({
    region: '',
    ppm: 0,
    arsenic: 0,
    dissolvedOxygen: 0,
    turbidity: 0,
    temperature: 0,
    pH: 7,
    conductivity: 0,
    salinity: 0,
    tds: 0,
    hardness: 0,
    acidity: 0,
    cdom: 0,
  });
  const [locationData, setLocationData] = useState<WaterQualityByLocationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);

  const loadData = async () => {
    try {
      // Fetch water data (optionally you could pass a region)
      const data = await fetchWaterData();
      setWaterData(data);
    } catch (error) {
      console.error('Error fetching water data:', error);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createWaterData(formData);
      // Reload data after successful POST
      loadData();
      // Reset form
      setFormData({
        region: '',
        ppm: 0,
        arsenic: 0,
        dissolvedOxygen: 0,
        turbidity: 0,
        temperature: 0,
        pH: 7,
        conductivity: 0,
        salinity: 0,
        tds: 0,
        hardness: 0,
        acidity: 0,
        cdom: 0,
      });
    } catch (error) {
      console.error('Error creating water data:', error);
    }
  };

  const handleLocateMe = () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser. Please use a modern browser that supports geolocation.');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;

          // Get location information using our enhanced geocoding service with multiple fallbacks
          let locationInfo = null;
          try {
            locationInfo = await getLocationFromCoordinates(latitude, longitude);
            console.log('Water dashboard location data retrieved:', locationInfo);
          } catch (locationError) {
            console.error('Error getting location information:', locationError);
            // We'll proceed without the location info
          }

          // Get water quality data with retry logic (will use fallback if API fails)
          try {
            const data = await getWaterQualityWithRetry(latitude, longitude);

            // If we have location info but the API didn't provide it, enhance the data
            if (data && locationInfo && data.currentLocation && (!data.currentLocation.fullLocation || !data.currentLocation.fullLocation.city)) {
              data.currentLocation.fullLocation = {
                city: locationInfo.city,
                state: locationInfo.state,
                country: locationInfo.country,
                formatted: locationInfo.formatted || data.currentLocation.fullLocation?.formatted || '',
                latitude,
                longitude
              };
            }

            setLocationData(data);
          } catch (error) {
            // This should never happen now since getWaterQualityWithRetry will return fallback data
            console.error('Unexpected error getting water quality data:', error);

            // Use fallback data directly in this case
            const fallbackData = generateFallbackWaterData(latitude, longitude);

            // If we have location info, enhance the fallback data
            if (locationInfo) {
              fallbackData.currentLocation.fullLocation = {
                city: locationInfo.city,
                state: locationInfo.state,
                country: locationInfo.country,
                formatted: locationInfo.formatted,
                latitude,
                longitude
              };
            }

            setLocationData(fallbackData);
          }

          setLoading(false);
        } catch (error) {
          console.error('Error processing location:', error);
          // Even in this unexpected case, try to provide some data
          try {
            const fallbackData = generateFallbackWaterData(0, 0);
            fallbackData.waterTips.unshift('We were unable to determine your exact location. The information below is general guidance.');
            setLocationData(fallbackData);
            setError('Could not process your location accurately. Showing general water quality information instead.');
          } catch (fallbackError) {
            setError('An unexpected error occurred. Please try again later.');
          }
          setLoading(false);
        }
      },
      (err) => {
        console.error('Geolocation error:', err);

        // Even when geolocation fails, provide some general water information
        try {
          const fallbackData = generateFallbackWaterData(0, 0);
          fallbackData.waterTips.unshift('We were unable to access your location. The information below is general guidance.');
          setLocationData(fallbackData);
          setError('Could not access your location. Showing general water quality information instead.');
        } catch (fallbackError) {
          setError('Could not access your location. Please check your browser permissions and try again.');
        }

        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Add this function to generate default water quality data when the API fails
  const generateFallbackWaterData = (latitude: number, longitude: number): WaterQualityByLocationResponse => {
    // Create a generic region name based on coordinates
    const region = `Region near ${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;

    // Default water data with reasonable values
    const defaultWaterData: WaterData = {
      region: region,
      ppm: 150, // Average PPM
      arsenic: 0.005, // Low arsenic level
      dissolvedOxygen: 8.5, // Good dissolved oxygen
      turbidity: 5, // Average turbidity
      temperature: 20, // Average temperature
      pH: 7.2, // Neutral pH
      conductivity: 500, // Average conductivity
      salinity: 0.5, // Low salinity
      tds: 300, // Average TDS
      hardness: 120, // Moderately hard
      acidity: 3, // Low acidity
      cdom: 10 // Average CDOM
    };

    // Create a default response with general tips
    return {
      currentLocation: {
        region: region,
        fullLocation: {
          city: null,
          state: null,
          country: null,
          formatted: `Approximation for coordinates: ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
          latitude,
          longitude
        },
        data: defaultWaterData,
        qualityScore: 65 // Average quality score
      },
      nearestBetterQuality: null, // No better quality data available
      betterQualityLocations: [],
      waterTips: [
        "This is general water quality information as we couldn't retrieve specific data for your location.",
        "For drinking water, consider using a home filtration system to remove potential contaminants.",
        "Regularly clean your water storage containers to prevent bacterial growth.",
        "If your water appears cloudy or has an unusual odor, contact your local water authority.",
        "Consider testing your water annually for contaminants if you use a private well."
      ]
    };
  };

  // Update the getWaterQualityWithRetry function to use the fallback as a last resort
  const getWaterQualityWithRetry = async (latitude: number, longitude: number, maxRetries = 3): Promise<WaterQualityByLocationResponse> => {
    let lastError;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        console.log(`Attempting to get water quality data (attempt ${attempt + 1}/${maxRetries})`);
        const data = await getWaterQualityByLocation(latitude, longitude);
        return data;
      } catch (error) {
        console.error(`Attempt ${attempt + 1} failed:`, error);
        lastError = error;
        // Wait between retries (increasing delay with each attempt)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }
    }

    // If all retries fail, use fallback data instead of throwing an error
    console.warn('All attempts failed, using fallback water quality data');
    return generateFallbackWaterData(latitude, longitude);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Water Quality Dashboard
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="water dashboard tabs">
          <Tab icon={<MyLocationIcon />} label="Locate Me" id="water-tab-0" aria-controls="water-tabpanel-0" />
          <Tab icon={<MapIcon />} label="Heat Map" id="water-tab-1" aria-controls="water-tabpanel-1" />
          <Tab icon={<TableChartIcon />} label="Data Table" id="water-tab-2" aria-controls="water-tabpanel-2" />
          <Tab icon={<AddIcon />} label="Add Data" id="water-tab-3" aria-controls="water-tabpanel-3" />
        </Tabs>
      </Box>

      {/* Locate Me Tab */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Check Water Quality Near You
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Click the button below to get water quality information for your current location,
              find better quality water nearby, and receive personalized water tips.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <MyLocationIcon />}
              onClick={handleLocateMe}
              disabled={loading}
              sx={{ minWidth: '150px' }}
            >
              {loading ? 'Getting Location...' : 'Locate Me'}
            </Button>

            {error && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleLocateMe}
                    >
                      Retry
                    </Button>
                  }
                >
                  {error}
                </Alert>

                <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Troubleshooting Tips:
                  </Typography>
                  <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                    <li>Check that your browser has permission to access your location</li>
                    <li>Ensure you have a stable internet connection</li>
                    <li>Try refreshing the page and allowing location access when prompted</li>
                    <li>If using a VPN, try disabling it temporarily</li>
                    <li>Clear your browser cache and cookies</li>
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => window.location.reload()}
                    >
                      Refresh Page
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setTabValue(1)}
                    >
                      Try Heat Map Instead
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            {locationData && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6">
                  Your Location: {locationData.currentLocation.fullLocation?.formatted || locationData.currentLocation.region}
                  {locationData.currentLocation.fullLocation?.formatted?.includes('Approximation') && (
                    <Chip
                      label="Approximate Data"
                      color="warning"
                      size="small"
                      sx={{ ml: 1, verticalAlign: 'middle' }}
                    />
                  )}
                </Typography>

                {locationData.waterTips.some(tip => tip.includes("couldn't retrieve specific data")) && (
                  <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                    We're showing general water quality information since specific data for your location couldn't be retrieved.
                    This data is for educational purposes only and may not reflect actual conditions in your area.
                  </Alert>
                )}

                {locationData.currentLocation.data && (
                  <Card variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        <WaterDropIcon /> Current Water Quality
                        {locationData.waterTips.some(tip => tip.includes("general")) && (
                          <Chip
                            label="General Information"
                            color="info"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="body2">
                          PPM: {locationData.currentLocation.data.ppm}
                        </Typography>
                        <Typography variant="body2">
                          pH: {locationData.currentLocation.data.pH}
                        </Typography>
                        <Typography variant="body2">
                          Arsenic: {locationData.currentLocation.data.arsenic} mg/L
                        </Typography>
                        <Typography variant="body2">
                          Turbidity: {locationData.currentLocation.data.turbidity} NTU
                        </Typography>
                        <Typography variant="body2">
                          Temperature: {locationData.currentLocation.data.temperature}°C
                        </Typography>
                        <Typography variant="body2">
                          Hardness: {locationData.currentLocation.data.hardness} mg/L
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {locationData.nearestBetterQuality && (
                  <Card variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        <NavigationIcon /> Nearest Better Quality Water
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Region: {locationData.nearestBetterQuality.region}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="body2">
                          PPM: {locationData.nearestBetterQuality.ppm}
                        </Typography>
                        <Typography variant="body2">
                          pH: {locationData.nearestBetterQuality.pH}
                        </Typography>
                        <Typography variant="body2">
                          Arsenic: {locationData.nearestBetterQuality.arsenic} mg/L
                        </Typography>
                        <Typography variant="body2">
                          Turbidity: {locationData.nearestBetterQuality.turbidity} NTU
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      <TipsAndUpdatesIcon /> Water Quality Tips
                    </Typography>
                    <List dense>
                      {locationData.waterTips.map((tip, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <WaterDropIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={tip} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Heat Map Tab */}
      <TabPanel value={tabValue} index={1}>
        <WaterHeatmap />
      </TabPanel>

      {/* Data Table Tab */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Region</TableCell>
                <TableCell>PPM</TableCell>
                <TableCell>Arsenic</TableCell>
                <TableCell>Dissolved Oxygen</TableCell>
                <TableCell>Turbidity</TableCell>
                <TableCell>Temperature</TableCell>
                <TableCell>pH</TableCell>
                <TableCell>Conductivity</TableCell>
                <TableCell>Salinity</TableCell>
                <TableCell>TDS</TableCell>
                <TableCell>Hardness</TableCell>
                <TableCell>Acidity</TableCell>
                <TableCell>CDOM</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {waterData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.region}</TableCell>
                  <TableCell>{item.ppm}</TableCell>
                  <TableCell>{item.arsenic}</TableCell>
                  <TableCell>{item.dissolvedOxygen}</TableCell>
                  <TableCell>{item.turbidity}</TableCell>
                  <TableCell>{item.temperature}</TableCell>
                  <TableCell>{item.pH}</TableCell>
                  <TableCell>{item.conductivity}</TableCell>
                  <TableCell>{item.salinity}</TableCell>
                  <TableCell>{item.tds}</TableCell>
                  <TableCell>{item.hardness}</TableCell>
                  <TableCell>{item.acidity}</TableCell>
                  <TableCell>{item.cdom}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Add Data Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}
        >
          <Typography variant="h6" sx={{ width: '100%' }}>
            Add New Water Data
          </Typography>

          <TextField
            label="Region"
            name="region"
            value={formData.region}
            onChange={handleChange}
          />
          <TextField
            label="PPM"
            name="ppm"
            type="number"
            value={formData.ppm}
            onChange={handleChange}
          />
          <TextField
            label="Arsenic"
            name="arsenic"
            type="number"
            value={formData.arsenic}
            onChange={handleChange}
          />
          <TextField
            label="Dissolved Oxygen"
            name="dissolvedOxygen"
            type="number"
            value={formData.dissolvedOxygen}
            onChange={handleChange}
          />
          <TextField
            label="Turbidity"
            name="turbidity"
            type="number"
            value={formData.turbidity}
            onChange={handleChange}
          />
          <TextField
            label="Temperature"
            name="temperature"
            type="number"
            value={formData.temperature}
            onChange={handleChange}
          />
          <TextField
            label="pH"
            name="pH"
            type="number"
            value={formData.pH}
            onChange={handleChange}
          />
          <TextField
            label="Conductivity"
            name="conductivity"
            type="number"
            value={formData.conductivity}
            onChange={handleChange}
          />
          <TextField
            label="Salinity"
            name="salinity"
            type="number"
            value={formData.salinity}
            onChange={handleChange}
          />
          <TextField
            label="TDS"
            name="tds"
            type="number"
            value={formData.tds}
            onChange={handleChange}
          />
          <TextField
            label="Hardness"
            name="hardness"
            type="number"
            value={formData.hardness}
            onChange={handleChange}
          />
          <TextField
            label="Acidity"
            name="acidity"
            type="number"
            value={formData.acidity}
            onChange={handleChange}
          />
          <TextField
            label="CDOM"
            name="cdom"
            type="number"
            value={formData.cdom}
            onChange={handleChange}
          />

          <Button variant="contained" type="submit" sx={{ mt: 2 }}>
            Submit
          </Button>
        </Box>
      </TabPanel>
    </Box>
  );
};

export default WaterDashboard;

